/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  CHILD_REQUEST_PAYMENT: 'transition/child-request-payment',
  REQUEST_RESCHEDULE: 'transition/request-reschedule',
  ACCEPT_RESCHEDULE: 'transition/accept-reschedule',
  CANCEL_RESCHEDULE: 'transition/cancel-reschedule',
  // Mobile-only self-serve reschedule (no merchant approval step) -- web has
  // no UI for this and doesn't fire it, but a transaction rescheduled via
  // mobile is still viewed here, so it needs a label in isRelevantPastTransition
  // below rather than showing as unlabeled history.
  CUSTOMER_RESCHEDULE: 'transition/customer-reschedule',
  // Mobile-only self-serve cancellation (no merchant approval step) -- web
  // has no UI for this and doesn't fire it, but a transaction cancelled via
  // mobile is still viewed here, so it needs a label below rather than
  // showing as unlabeled history.
  CUSTOMER_CANCEL: 'transition/customer-cancel',
  DISPUTE: 'transition/dispute',
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',
  RECEIVED_FROM_DISPUTED: 'transition/received-from-disputed',
  AUTO_COMPLETE: 'transition/auto-complete',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Negotiation (quote) flow, for listings on the "negotiated" transaction
  // model. The provider answers an inquiry with a priced offer and both sides
  // may counter until a price is agreed; the loop then rejoins the ordinary
  // booking flow at pending-payment.
  MAKE_OFFER_AFTER_INQUIRY: 'transition/make-offer-after-inquiry',
  CUSTOMER_MAKE_COUNTER_OFFER: 'transition/customer-make-counter-offer',
  PROVIDER_MAKE_COUNTER_OFFER: 'transition/provider-make-counter-offer',
  // Rejects the customer's counter and reinstates the provider's own offer.
  // It returns to offer-pending, it does not end the negotiation.
  PROVIDER_REJECT_COUNTER_OFFER: 'transition/provider-reject-counter-offer',
  CUSTOMER_ACCEPT_OFFER: 'transition/customer-accept-offer',
  PROVIDER_ACCEPT_OFFER: 'transition/provider-accept-offer',
  CUSTOMER_REJECT_OFFER: 'transition/customer-reject-offer',
  PROVIDER_WITHDRAW_OFFER: 'transition/provider-withdraw-offer',
  OPERATOR_REJECT_OFFER: 'transition/operator-reject-offer',
  OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER:
    'transition/operator-reject-from-customer-counter-offer',
  REQUEST_PAYMENT_TO_ACCEPT_OFFER: 'transition/request-payment-to-accept-offer',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  CHILD_CONFIRM_PAYMENT: 'transition/child-confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  AUTO_ACCEPT: 'transition/auto-accept',
  DECLINE: 'transition/decline',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // The backend automatically cancel the transaction.
  AUTO_CANCEL: 'transition/auto-cancel',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  // Negotiation (quote) flow
  OFFER_PENDING: 'offer-pending',
  CUSTOMER_OFFER_PENDING: 'customer-offer-pending',
  OFFER_REJECTED: 'offer-rejected',
  OFFER_ACCEPTED: 'offer-accepted',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  REQUESTED_RESCHEDULE: 'requested-reschedule',
  EXPIRED: 'expired',
  CANCELED: 'cancelled',
  DELIVERED: 'delivered',
  DISPUTED: 'disputed',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.CHILD_REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.MAKE_OFFER_AFTER_INQUIRY]: states.OFFER_PENDING,
      },
    },

    [states.OFFER_PENDING]: {
      on: {
        [transitions.CUSTOMER_MAKE_COUNTER_OFFER]: states.CUSTOMER_OFFER_PENDING,
        [transitions.CUSTOMER_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        [transitions.CUSTOMER_REJECT_OFFER]: states.OFFER_REJECTED,
        [transitions.PROVIDER_WITHDRAW_OFFER]: states.OFFER_REJECTED,
        [transitions.OPERATOR_REJECT_OFFER]: states.OFFER_REJECTED,
      },
    },

    [states.CUSTOMER_OFFER_PENDING]: {
      on: {
        [transitions.PROVIDER_MAKE_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_REJECT_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        [transitions.OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER]: states.OFFER_REJECTED,
      },
    },

    [states.OFFER_REJECTED]: {},

    // Where the negotiation loop rejoins the ordinary booking flow.
    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.REQUEST_PAYMENT_TO_ACCEPT_OFFER]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.CHILD_CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        [transitions.AUTO_ACCEPT]: states.ACCEPTED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.CUSTOMER_CANCEL]: states.CANCELED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
        [transitions.REQUEST_RESCHEDULE]: states.REQUESTED_RESCHEDULE,
        // Mobile-only self-serve reschedule -- self-loop, no merchant
        // approval step. Web has no UI to fire this but must still know
        // accepted -> accepted for it so getState() resolves correctly for
        // a transaction rescheduled via mobile and then viewed here.
        [transitions.CUSTOMER_RESCHEDULE]: states.ACCEPTED,
      },
    },

    [states.REQUESTED_RESCHEDULE]: {
      on: {
        [transitions.ACCEPT_RESCHEDULE]: states.ACCEPTED,
        [transitions.CANCEL_RESCHEDULE]: states.ACCEPTED,
      },
    },

    [states.DELIVERED]: {
      on: {
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.CANCEL_FROM_DISPUTED]: states.COMPLETED,
        [transitions.RECEIVED_FROM_DISPUTED]: states.COMPLETED,
      },
    },

    [states.CANCELED]: {},

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.AUTO_ACCEPT,
    transitions.REQUEST_RESCHEDULE,
    transitions.ACCEPT_RESCHEDULE,
    transitions.CANCEL_RESCHEDULE,
    transitions.CUSTOMER_RESCHEDULE,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    transitions.CUSTOMER_CANCEL,
    transitions.DISPUTE,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.RECEIVED_FROM_DISPUTED,
    transitions.AUTO_COMPLETE,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.CHILD_CONFIRM_PAYMENT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    // Negotiation. REQUEST_PAYMENT_TO_ACCEPT_OFFER is left out on purpose:
    // CONFIRM_PAYMENT above already covers that beat in the feed.
    transitions.MAKE_OFFER_AFTER_INQUIRY,
    transitions.CUSTOMER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_REJECT_COUNTER_OFFER,
    transitions.CUSTOMER_ACCEPT_OFFER,
    transitions.PROVIDER_ACCEPT_OFFER,
    transitions.CUSTOMER_REJECT_OFFER,
    transitions.PROVIDER_WITHDRAW_OFFER,
    transitions.OPERATOR_REJECT_OFFER,
    transitions.OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.CHILD_REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    // Every transition that sets an offer price must be priced server-side.
    transitions.MAKE_OFFER_AFTER_INQUIRY,
    transitions.CUSTOMER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_REJECT_COUNTER_OFFER,
    transitions.REQUEST_PAYMENT_TO_ACCEPT_OFFER,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.RECEIVED_FROM_DISPUTED,
    transitions.AUTO_COMPLETE,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    // A refund now IS triggered for this -- synchronously, from
    // server/api-util/cancellationSettlement.js, right after the transition
    // succeeds (see transition-privileged.js#handleCancellationTransition).
    // Note payoutTotal/payinTotal below still reflect the pre-cancellation
    // amounts, since Sharetribe's own line-item reversal never fires for
    // this transition -- the real numbers live in
    // transaction.attributes.metadata.cancellationSettlement, rendered by
    // CancellationSettlementMaybe.js, not in this label's totalPrice.
    transitions.CUSTOMER_CANCEL,
    transitions.DECLINE,
  ];
  return txRefundedTransitions.includes(transition);
};

// INQUIRY and CUSTOMER_OFFER_PENDING are the negotiation states where the
// provider is the one who has to move — answer the inquiry with an offer, and
// respond to the customer's counter.
export const statesNeedingProviderAttention = [
  states.PREAUTHORIZED,
  states.INQUIRY,
  states.CUSTOMER_OFFER_PENDING,
];
